<template>
  <div class="mortgage-progress-edit-component" :class="{loaded}">
    <a-row :gutter="16">
      <a-col :span="12">

        <h4>Aan te leveren stukken</h4>
        <template v-if="loaded">
          <MortgageProgressEditItemComponent v-for="mortgageProgressItem in mortgageProgressToBeReceived"
                                             :key="mortgageProgressItem.id"
                                             :mortgage-progress="mortgageProgressItem"
                                             @delete="onMortgageProgressDelete" />
          <a-empty v-if="mortgageProgressToBeReceived.length < 1"
                   description="Er zijn geen stukken geselecteerd"
                   style="margin: 10px 0;" />
        </template>
        <Skeleton v-else class="skeleton" :loading="true" v-for="index in 2" :key="index" />

        <h4>Te versturen naar maatschappij</h4>
        <template v-if="loaded">
          <MortgageProgressEditItemComponent v-for="mortgageProgressItem in mortgageProgressReceived"
                                             :key="mortgageProgressItem.id"
                                             :mortgage-progress="mortgageProgressItem"
                                             @delete="onMortgageProgressDelete" />
          <a-empty v-if="mortgageProgressReceived.length < 1"
                   description="Er staan geen stukken klaar om naar de maatschappij te versturen"
                   style="margin: 10px 0;" />
        </template>
        <Skeleton v-else class="skeleton" :loading="true" v-for="index in 2" :key="index" />

        <h4>Goed te keuren door maatschappij</h4>
        <template v-if="loaded">
          <MortgageProgressEditItemComponent v-for="mortgageProgressItem in mortgageProgressSent"
                                             :key="mortgageProgressItem.id"
                                             :mortgage-progress="mortgageProgressItem"
                                             @delete="onMortgageProgressDelete" />
          <a-empty v-if="mortgageProgressSent.length < 1"
                   description="Er zijn geen stukken die wachten op goedkeuring door de maatschappij"
                   style="margin: 10px 0;" />
        </template>
        <Skeleton v-else class="skeleton" :loading="true" v-for="index in 2" :key="index" />

        <h4>Goedgekeurd</h4>
        <template v-if="loaded">
          <MortgageProgressEditItemComponent v-for="mortgageProgressItem in mortgageProgressCompleted"
                                             :key="mortgageProgressItem.id"
                                             :mortgage-progress="mortgageProgressItem"
                                             @delete="onMortgageProgressDelete" />
          <a-empty v-if="mortgageProgressCompleted.length < 1" description="Er zijn nog geen goedgekeurde stukken"
                   style="margin: 10px 0;" />
        </template>
        <Skeleton v-else class="skeleton" :loading="true" v-for="index in 2" :key="index" />

      </a-col>
      <a-col :span="12">
        <h4>Stukkenlijsten</h4>
        <document-type-selectable-list-component @select="onDocumentTypeSelect"
                                                 @select-custom="onCustomTypeSelect"
                                                 @select-multiple="onDocumentTypesSelect" />
      </a-col>
    </a-row>
  </div>
</template>

<script>
import mortgageApi from "~/api/mortgage";
import mortgageProgressApi from "~/api/mortgage_progress";
import documentTypeApi from "~/api/document_type";
import { Skeleton } from "vue-loading-skeleton";
import MortgageProgressEditItemComponent from "./EditItemComponent";

export default {
  name: "MortgageProgressEditComponent",
  components: {
    MortgageProgressEditItemComponent,
    Skeleton
  },
  props: {
    mortgageId: {
      required: false,
      default: null
    },
    mortgageProp: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      mortgageProgress: [],
      documentTypes: [],
      mortgageProgressGroup: null,
      loaded: false,
      documentTypesLoaded: false,
      searchQuery: "",
      sortType: "hdn",
      mortgage: null,
    };
  },
  mounted() {
    if (this.mortgageProp === null && this.mortgageId === null) {
      throw new Error("Either the mortgage object or mortgage id is required.");
    }

    if (this.mortgageProp === null) {
      mortgageApi.find(this.mortgageId)
        .then(response => {
          this.mortgage = response.data;
          this.initMortgageProgress()
            .then(() => {
              this.loaded = true;
            });
        });
    } else {
      this.mortgage = this.mortgageProp;
      this.initMortgageProgress()
        .then(() => {
          this.loaded = true;
        });
    }

    this.initDocumentTypes()
      .then(() => {
        this.documentTypesLoaded = true;
      });
  },
  computed: {
    mortgageProgressToBeReceived() {
      return this.mortgageProgress.filter(
        item =>
          item.received === false
          && item.sent === false
          && item.completed === false
      );
    },
    mortgageProgressReceived() {
      return this.mortgageProgress.filter(item => item.received === true && item.sent === false && item.completed === false);
    },
    mortgageProgressCompleted() {
      return this.mortgageProgress.filter(item => item.received === true && item.sent === true && item.completed === true);
    },
    mortgageProgressSent() {
      return this.mortgageProgress.filter(item => item.received === true && item.sent === true && item.completed === false);
    },
    mortgageProgressEmpty() {
      return this.mortgageProgressToBeReceived.length === 0;
    }
  },
  methods: {
    initMortgageProgress() {
      return mortgageProgressApi.getByMortgage(this.mortgage.id)
        .then(response => {
          this.mortgageProgress = response.data;
        });
    },
    initDocumentTypes() {
      return documentTypeApi.getAllWithHDN()
        .then(response => {
          this.documentTypes = response.data;
        });
    },
    onMortgageProgressDelete(mortgageProgressItem) {
      mortgageProgressApi.delete(mortgageProgressItem.id)
        .then(() => {
          Object.assign(this, {
            mortgageProgress: this.mortgageProgress.filter(item => {
              return item.id !== mortgageProgressItem.id;
            })
          });
        });
    },
    onProgressGroupChange(mortgageProgressGroup) {
      this.mortgageProgressGroup = mortgageProgressGroup;
    },
    onDocumentTypeSelect(documentType, documentTypeItemComponent) {
      mortgageProgressApi.create({
        mortgage_id: this.mortgage.id,
        name: documentType.name,
        document_type_id: documentType.id
      })
        .then(() => {
          documentTypeItemComponent.loading = false;
          this.initMortgageProgress();
        });
    },
    onCustomTypeSelect(customType, documentTypeItemComponent) {
      mortgageProgressApi.create({
        mortgage_id: this.mortgage.id,
        name: customType
      })
        .then(() => {
          documentTypeItemComponent.loadingCustom = false;
          this.initMortgageProgress();
        });
    },
    onDocumentTypesSelect(documentTypes, documentTypeSelectableListComponent) {
      mortgageProgressApi.createByDocumentTypes(this.mortgage.id, documentTypes)
        .then(() => {
          documentTypeSelectableListComponent.loadingAll = false;
          this.initMortgageProgress();
        });
    }
  }
};
</script>
<style scoped>
.mortgage-progress-edit-component {
  position: relative;
  margin-bottom: 15px;
}

.mortgage-progress-edit-component .search-input {
  margin: 15px 0;
}

.create-button {
  margin-top: 15px;
}

.document-type-list {
  margin-bottom: 15px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  line-height: 1.5;
  overflow-y: auto;
  max-height: 400px;
}

.document-type-list-item {
  padding: 8px 16px;
  border-bottom: 1px solid #e8e8e8;
}

.document-type-list-item .faded {
  opacity: 0.4;
  font-family: monospace;
  margin-right: 10px;
}

.document-type-list-item:last-child {
  border-bottom: none;
}

.document-type-list-item:hover {
  background-color: #ececec;
  cursor: pointer;
}

.skeleton {
  height: 44px;
  display: block;
  margin-bottom: 5px;
}
</style>
